import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import { buildWhatsappUrl, openWhatsapp, WA_MESSAGES, formatEUR } from "../lib/contact";
import { MessageCircle, Sparkles, Upload, X, Loader2, BadgeCheck, AlertTriangle } from "lucide-react";

const MAKES = ["Audi", "BMW", "Mercedes-Benz", "Porsche", "Tesla", "Volkswagen", "Skoda", "Ford", "Opel", "Renault", "Peugeot", "Hyundai", "Kia", "Mazda", "Toyota", "Nissan", "Volvo", "Andere"];
const FUELS = ["Benzin", "Diesel", "Elektro", "Hybrid", "Gas"];
const TRANS = ["Automatik", "Manuell", "DSG", "Halbautomatik"];
const CONDITIONS = [
  "Sehr gut", "Gut", "Gebrauchsspuren",
  "Beschädigt", "Nicht fahrbereit", "Motorschaden", "Getriebeschaden",
  "Unfallschaden", "Karosserieschaden", "Elektronikproblem", "Sonstiger Defekt",
];
const ROADWORTHY = ["Ja", "Nein", "Eingeschränkt fahrbereit"];
const FLAGS = ["Serviceheft vorhanden", "Unfallfrei", "Pickerl gültig", "Importfahrzeug", "Leasing / Finanzierung offen"];

const isDamaged = (condition, roadworthy) => {
  if (roadworthy === "Nein") return true;
  return [
    "Beschädigt", "Nicht fahrbereit", "Motorschaden", "Getriebeschaden",
    "Unfallschaden", "Karosserieschaden", "Elektronikproblem", "Sonstiger Defekt",
  ].includes(condition);
};

const PurchaseRequestForm = () => {
  const [form, setForm] = React.useState({
    make: "BMW",
    model: "",
    year: 2018,
    mileage: 80000,
    fuel: "Diesel",
    transmission: "Automatik",
    power_hp: 150,
    condition: "Gut",
    roadworthy: "Ja",
    flags: [],
    description: "",
    photos: [],
    name: "",
    phone: "",
    email: "",
    location: "",
  });
  const [valuation, setValuation] = React.useState(null);
  const [loadingAI, setLoadingAI] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFlag = (flag) =>
    setForm((f) => ({
      ...f,
      flags: f.flags.includes(flag) ? f.flags.filter((x) => x !== flag) : [...f.flags, flag],
    }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data.url);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...uploaded] }));
      toast.success(`${uploaded.length} Foto(s) hochgeladen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const runAI = async () => {
    if (!form.make || !form.model || !form.year || !form.mileage) {
      toast.error("Bitte mindestens Marke, Modell, Baujahr und Kilometer angeben.");
      return;
    }
    setLoadingAI(true);
    setValuation(null);
    try {
      const { data } = await api.post("/ai-valuation", {
        make: form.make,
        model: form.model,
        year: Number(form.year),
        mileage: Number(form.mileage),
        fuel: form.fuel,
        transmission: form.transmission,
        power_hp: Number(form.power_hp || 0),
        condition: form.condition,
        roadworthy: form.roadworthy,
        flags: form.flags,
        description: form.description,
      });
      setValuation(data);
    } catch (err) {
      toast.error("KI-Bewertung konnte nicht abgerufen werden");
    } finally {
      setLoadingAI(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Bitte Name und Telefonnummer ausfüllen.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/purchase-requests", {
        ...form,
        year: Number(form.year),
        mileage: Number(form.mileage),
        power_hp: Number(form.power_hp || 0),
        ai_price_min: valuation?.price_min ?? null,
        ai_price_max: valuation?.price_max ?? null,
        ai_reasoning: valuation?.reasoning ?? null,
      });
      setSubmitted(true);
      toast.success("Vielen Dank! Wir melden uns mit einem persönlichen Angebot.");
    } catch (err) {
      toast.error("Anfrage konnte nicht gesendet werden");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#232330] border border-[var(--na-red)]/30 rounded-2xl p-8 text-center space-y-4">
        <div className="w-14 h-14 mx-auto rounded-full bg-[var(--na-red)]/15 text-[var(--na-red)] flex items-center justify-center">
          <BadgeCheck className="w-7 h-7" />
        </div>
        <h3 className="font-display text-2xl font-semibold text-white">Anfrage erhalten</h3>
        <p className="text-zinc-400 max-w-md mx-auto">
          Wir prüfen Ihre Angaben und melden uns innerhalb eines Werktags mit einem konkreten Angebot.
          Sie können uns auch direkt per WhatsApp kontaktieren.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <a
            href={buildWhatsappUrl(WA_MESSAGES.sellCar)}
            onClick={openWhatsapp(WA_MESSAGES.sellCar)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sell-success-whatsapp"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#25D366] text-[#1A1A22] font-medium"
          >
            <MessageCircle className="w-4 h-4" /> Direkt per WhatsApp senden
          </a>
          <button
            onClick={() => { setSubmitted(false); setValuation(null); }}
            data-testid="sell-success-new-request"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white"
          >
            Neue Bewertung
          </button>
        </div>
      </div>
    );
  }

  const damagedTone = isDamaged(form.condition, form.roadworthy);

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="purchase-request-form">
      {/* Section 1: Vehicle data */}
      <section className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6 lg:p-8 space-y-5">
        <SectionHeader number="01" title="Fahrzeugdaten" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DSelect testId="sell-make" label="Marke" value={form.make} onValueChange={(v) => update("make", v)} options={MAKES} />
          <DInput testId="sell-model" label="Modell" value={form.model} onChange={(e) => update("model", e.target.value)} placeholder="z.B. 530d xDrive" />
          <DInput testId="sell-year" label="Baujahr" type="number" value={form.year} onChange={(e) => update("year", e.target.value)} />
          <DInput testId="sell-mileage" label="Kilometerstand" type="number" value={form.mileage} onChange={(e) => update("mileage", e.target.value)} />
          <DSelect testId="sell-fuel" label="Kraftstoff" value={form.fuel} onValueChange={(v) => update("fuel", v)} options={FUELS} />
          <DSelect testId="sell-transmission" label="Getriebe" value={form.transmission} onValueChange={(v) => update("transmission", v)} options={TRANS} />
          <DInput testId="sell-power" label="Leistung (PS)" type="number" value={form.power_hp} onChange={(e) => update("power_hp", e.target.value)} />
          <DSelect testId="sell-condition" label="Fahrzeugzustand" value={form.condition} onValueChange={(v) => update("condition", v)} options={CONDITIONS} />
          <DSelect testId="sell-roadworthy" label="Fahrbereit?" value={form.roadworthy} onValueChange={(v) => update("roadworthy", v)} options={ROADWORTHY} />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-3 block">Merkmale</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FLAGS.map((flag) => (
              <label key={flag} className="flex items-start gap-2.5 p-3 rounded-xl border border-white/10 bg-[#1A1A22] cursor-pointer hover:border-white/20 transition-colors">
                <Checkbox
                  data-testid={`sell-flag-${flag.replace(/\s+/g, '-').toLowerCase()}`}
                  checked={form.flags.includes(flag)}
                  onCheckedChange={() => toggleFlag(flag)}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--na-red)] data-[state=checked]:border-[var(--na-red)]"
                />
                <span className="text-sm text-zinc-300 leading-tight">{flag}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">
            Beschreibung des Schadens / Problems
          </Label>
          <textarea
            data-testid="sell-description"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={4}
            placeholder={damagedTone
              ? "Beschreiben Sie den Schaden möglichst genau (Art, Umfang, Reparaturhistorie)…"
              : "Optional: Besonderheiten, Ausstattung, Servicehistorie…"}
            className="w-full rounded-xl bg-[#1A1A22] border border-white/10 text-white px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[var(--na-red)]"
          />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Fotos</Label>
          <div className="flex flex-wrap gap-3">
            {form.photos.map((p, i) => (
              <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10">
                <img src={resolveImageUrl(p)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  data-testid={`sell-photo-remove-${i}`}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/80 text-white flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label className="w-24 h-24 rounded-xl border border-dashed border-white/20 bg-[#1A1A22] flex flex-col items-center justify-center text-zinc-400 hover:text-white hover:border-white/40 cursor-pointer transition-colors">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-[10px] mt-1 uppercase tracking-widest">{uploading ? "…" : "Hinzufügen"}</span>
              <input type="file" accept="image/*,.heic,.heif" multiple onChange={onUpload} className="hidden" data-testid="sell-photo-upload" />
            </label>
          </div>
        </div>
      </section>

      {/* Section 2: AI valuation */}
      <section className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6 lg:p-8 space-y-5">
        <SectionHeader number="02" title="KI-Vorbewertung" accent />
        <p className="text-sm text-zinc-400">
          Erhalten Sie auf Basis Ihrer Angaben eine sofortige unverbindliche Preisspanne, ermittelt durch unsere KI.
          Die finale Bewertung erfolgt erst nach persönlicher Prüfung durch NA Automobile.
        </p>
        <Button
          type="button"
          onClick={runAI}
          disabled={loadingAI}
          data-testid="sell-ai-button"
          className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl tracking-wide"
        >
          {loadingAI ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />KI analysiert…</> : <><Sparkles className="w-4 h-4 mr-2" />KI-Einschätzung starten</>}
        </Button>

        {valuation && (
          <div
            data-testid="sell-ai-result"
            className="rounded-2xl border border-[var(--na-red)]/40 bg-gradient-to-br from-[var(--na-red)]/10 to-transparent p-5 lg:p-6 space-y-3"
          >
            <p className="text-[11px] uppercase tracking-widest text-[var(--na-red)] font-semibold">
              {damagedTone ? "Auf Basis der angegebenen Schäden" : "Unverbindliche KI-Einschätzung"}
            </p>
            <p className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">
              {formatEUR(valuation.price_min)} – {formatEUR(valuation.price_max)}
            </p>
            {valuation.reasoning && (
              <p className="text-sm text-zinc-300 leading-relaxed">{valuation.reasoning}</p>
            )}
            <p className="text-xs text-zinc-500 leading-relaxed pt-2 border-t border-white/10 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Die finale Bewertung erfolgt erst nach persönlicher Prüfung durch NA Automobile. Die KI-Einschätzung ist unverbindlich.
            </p>
          </div>
        )}
      </section>

      {/* Section 3: Contact */}
      <section className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6 lg:p-8 space-y-5">
        <SectionHeader number="03" title="Ihre Kontaktdaten" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DInput testId="sell-name" label="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} />
          <DInput testId="sell-phone" label="Telefon *" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
          <DInput testId="sell-email" label="E-Mail" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
          <DInput testId="sell-location" label="Ort / PLZ" value={form.location} onChange={(e) => update("location", e.target.value)} />
        </div>
      </section>

      <div className="flex flex-col sm:flex-row gap-3 sticky bottom-20 lg:static lg:bottom-auto z-10">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="sell-submit-button"
          className="flex-1 bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white font-medium rounded-xl py-3 tracking-wide red-glow"
        >
          {submitting ? "Wird gesendet…" : "Bewertung anfragen"}
        </Button>
        <a
          href={buildWhatsappUrl(WA_MESSAGES.sellCar)}
          onClick={openWhatsapp(WA_MESSAGES.sellCar)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sell-whatsapp-link"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#25D366] text-[#1A1A22] font-medium tracking-wide"
        >
          <MessageCircle className="w-4 h-4" /> Direkt per WhatsApp senden
        </a>
      </div>
    </form>
  );
};

const SectionHeader = ({ number, title, accent = false }) => (
  <div className="flex items-baseline gap-4">
    <span className={`font-display text-3xl font-light ${accent ? "text-[var(--na-red)]" : "text-zinc-700"}`}>{number}</span>
    <h3 className="font-display text-xl lg:text-2xl font-semibold text-white tracking-tight">{title}</h3>
  </div>
);

const DInput = ({ label, testId, ...rest }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    <Input data-testid={testId} {...rest} className="bg-[#1A1A22] border-white/10 text-white focus-visible:ring-[var(--na-red)] focus-visible:border-[var(--na-red)]" />
  </div>
);

const DSelect = ({ label, value, onValueChange, options, testId }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testId} className="bg-[#1A1A22] border-white/10 text-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#232330] border-white/10 text-white max-h-72">
        {options.map((o) => (<SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>))}
      </SelectContent>
    </Select>
  </div>
);

export default PurchaseRequestForm;
